import { Button } from "@mui/material";
import { Document, Font, Page, pdf, PDFDownloadLink, PDFViewer, StyleSheet, Text, View } from "@react-pdf/renderer";
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { useEffect, useRef, useState } from 'react';
import firebaseDb from "../firebase-config";
import { DAYS_OF_WEEK, formatDayMonthDateYear } from '../dateFormatting';

Font.register({ family: "Times-Bold" });

const styles = StyleSheet.create({
    pdfViewer: {
        width: '100%',
        height: "70vw"
    },
    page: {
        padding: "10%",
        paddingBottom: "12%",
        fontFamily: "Times-Bold",
    },
    heading: {
        textAlign: "center",
        marginBottom: "25px",
        fontSize: "22px"
    },
    table: {
        width: '100%',
    },
    tableHeading: {
        marginVertical: "10px",
        fontSize: "16px"
    },
    row: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        fontSize: '14px',
        textAlign: "center",
    },
    bookingTitle: {
        fontSize: '16px',
    },
    day: {
        width: '5%'
    },
    artist: {
        width: '44%',
        textAlign: "left"
    },
    gigTime: {
        width: '26%',
        textAlign: "left"
    },
    compensation: {
        width: '30%'
    },
    subjectArtist: {
        width: '42%',
        textAlign: "left"
    },
    artistInfo: {
        width: '68%',
        textAlign: "left"
    },
    subjectRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        fontSize: '13px',
        textAlign: "center",
        paddingBottom: '10px'
    },
    confirmationHeader: {
        fontSize: '15px',
        textAlign: "center",
        fontFamily: "Times-Bold",
        textTransform: "uppercase",
        marginBottom: "40px",
    },
    paragraph: {
        fontSize: '13px',
        textAlign: "center",
        marginBottom: "15px",
        lineHeight: "1.3",
    },
    documentField: {
        textDecoration: "underline",
    },
    phoneNumber: {
        textDecoration: "underline",
        color: "#000080"
    }
})

/*************************
* BOOKING LIST FUNCTIONS *
**************************/

export function BookingListData(month, year, venueID) {
    const [events, setEvents] = useState({});
    const [bookings, setBookings] = useState([]);

    useEffect(() => {
        const ref = firebaseDb
            .child('database/events')
            .orderByChild('venue_month_year')
            .equalTo(venueID + "__" + month + "__" + year);

        ref.on('value', (snapshot) => {
            setEvents(snapshot.val() ?? {});
        });

        return () => ref.off();
    }, [month, year, venueID]);

    useEffect(() => {
        let tempBookings = [];

        if (events && Object.keys(events).length !== 0) {
            tempBookings = Object.keys(events).map((key) => {
                const event = events[key];
                const { day, startTime, endTime, } = formatEventTimes(event);

                return {
                    day,
                    stage: events[key].stage,
                    startTime,
                    endTime,
                    price: events[key].price + ".00"
                };
            }).filter(b => b.stage !== undefined);
        }

        setBookings(tempBookings);
    }, [events]);

    return bookings;
}

export function BookingListGenerate({ bookings, venue, month, year }) {
    const fileName = `${venue.name === "Renaissance-Exchange" ? "Exchange" : venue.name} Booking List-${month} ${year} V1`;

    if (!bookings.length) return null;

    return (
        <Document title={fileName}>
            <Page style={styles.page}>
                <View style={styles.heading} fixed>
                        <Text style={styles.bookingTitle}>{venue.name === "Renaissance-Exchange" ? "The Exchange" : venue.name} Live Music Bookings</Text>
                            <Text style={styles.bookingTitle}>by Date</Text>
                            <Text style={styles.bookingTitle}>{month} {year}</Text> 
                        </View>
                        <View style={styles.table}>
                            <View style={[styles.row, styles.tableHeading]} fixed>
                                <Text style={styles.artist}>Artist</Text>
                                <Text style={styles.gigTime}>Gig Time</Text>
                                <Text style={styles.compensation}>Compensation</Text>
                            </View>
                            {bookings.sort((a, b) => a.day - b.day || a.startTime.substring(0, 2) - b.startTime.substring(0, 2)).map((booking, i) => (
                                <View key={i} style={styles.row} wrap={false}>
                                    <Text style={styles.artist}>{booking.day}-{booking.stage}</Text>
                                    <Text style={styles.gigTime}>
                                        {(booking.startTime.charAt(0) === "0") ? booking.startTime.substring(1) : booking.startTime} to {(booking.endTime.charAt(0) === "0") ? booking.endTime.substring(1) : booking.endTime}
                                    </Text>
                                    <Text style={styles.compensation}>${booking.price}</Text>
                                </View>
                            ))}
                        </View>
            </Page>
        </Document>
    );
}

export function BookingListViewer({ bookings, venue, month, year }) {
    const pdfDoc = BookingListGenerate({ bookings, venue, month, year });
    return pdfDoc ? <PDFViewer style={styles.pdfViewer}>{pdfDoc}</PDFViewer> : <span>No bookings available</span>;
}

export function BookingListDownload({ bookings, venue, month, year }) {
    const pdfDoc = BookingListGenerate({ bookings, venue, month, year });
    const fileName = `${venue.name === "Renaissance-Exchange" ? "Exchange" : venue.name} Booking List-${month} ${year} V1`;

    return pdfDoc ? (
        <PDFDownloadLink document={pdfDoc} fileName={fileName}>
            {({ loading }) => (
                <Button variant="contained">
                    {loading ? 'Loading Document...' : 'Download'}
                </Button>
            )}
        </PDFDownloadLink>
    ) : <span>No bookings available</span>;
}

/***************************
* ARTIST INVOICE FUNCTIONS *
****************************/

export function InvoiceData(month, year, venueID) {
    const [events, setEvents] = useState({});
    const [invoices, setInvoices] = useState([]);

    useEffect(() => {
        firebaseDb.child('database/events').orderByChild('venue_month_year').equalTo(venueID + "__" + month + "__" + year).on('value', (snapshot) => {
            setEvents((snapshot.val() !== undefined) ? snapshot.val() : {});
        });
    }, [month, year, venueID]);

    useEffect(() => {
        let tempInvoices = [];

        if (events !== null && Object.keys(events).length !== 0) {
            tempInvoices = Object.keys(events).map((key) => {
                const event = events[key];
                const splitCheck = event.splitCheck === true || event.splitCheck === "true";
                const { day, startTime, endTime, dateStr, dateForFile } = formatEventTimes(event);

                let performerText = events[key].performers;

                if (typeof performerText === "string") {
                    performerText = performerText.split(",").map(p => p.trim());
                }

                if (Array.isArray(performerText)) {
                    performerText = performerText.filter(p => p.length > 0);

                    if (splitCheck) {
                        const total = parseFloat(events[key].price);
                        const each = (total / 2).toFixed(2);
                        performerText = performerText.join(" & ") + ` (2 Checks-$${each} ea.)`;
                    } else {
                        performerText = performerText.join(", ");
                    }
                }

                return {
                    day,
                    stage: events[key].stage,
                    performers: events[key].performers,
                    performerText: performerText,
                    startTime,
                    endTime,
                    price: parseFloat(events[key].price + ".00").toFixed(2),
                    dateStr,
                    dateForFile,
                    splitCheck
                };
            });
            tempInvoices = tempInvoices.filter(tempInvoice => tempInvoice["stage"] !== undefined);
        }
        setInvoices(tempInvoices);
    }, [events]);

    return invoices;
}

export function InvoiceGenerate(invoice, venue) {
    const venueName = venue.name;
    const venueAddress = `${venue.address.street1}, ${venue.address.city}, ${venue.address.state} ${venue.address.zip}`;
    const venueCityState = `${venue.address.city}, ${venue.address.state}.`;
    const venueText = {
            "Renaissance-Exchange": "The Renaissance /Exchange Bar"
        };
    const fileTitle = `${venueName} Booking Invoice - ${invoice.dateForFile}`;

    return (
        <Document title={fileTitle}>
            <Page style={styles.page}>
                <View style={styles.confirmationHeader}>
                    <Text style={{ marginBottom: "15px" }}>{venueName} {venueCityState}</Text>
                    <Text style={{ marginBottom: "15px" }}>Live performance contract/confirmation</Text>
                    <Text style={{ marginBottom: "15px" }}>Invoice</Text>
                    <Text>musicmattersbookings.com</Text>
                </View>
                <View>
                    <Text textBreakStrategy="simple" style={styles.paragraph}>
                        <Text wrap={false} style={styles.documentField}>&nbsp;&nbsp;&nbsp;{invoice.performerText}&nbsp;&nbsp;&nbsp;</Text> artist/performers agree to perform live music at {venueText[venueName] !== undefined ? venueText[venueName] : venueName}, {venueAddress} on the evening of <Text wrap={false} style={styles.documentField}>&nbsp;&nbsp;&nbsp;{invoice.dateStr}&nbsp;&nbsp;&nbsp;</Text> between the listed hours of <Text wrap={false} style={styles.documentField}>&nbsp;&nbsp;&nbsp;{(invoice.startTime.charAt(0) === "0") ? invoice.startTime.substring(1) : invoice.startTime}pm to {(invoice.endTime.charAt(0) === "0") ? invoice.endTime.substring(1) : invoice.endTime}pm&nbsp;&nbsp;&nbsp;</Text> and {venueText[venueName] !== undefined ? venueText[venueName] : venueName} in {venueCityState} agrees to pay the above named artists <Text wrap={false} style={styles.documentField}> ${invoice.price} </Text> and said payment to be paid upon completion of this performance.
                    </Text>
                </View>
            </Page>
        </Document>
    );
}

export function InvoicesViewer({ invoices, venue }) {
    if (!invoices || invoices.length === 0) {
        return <span>No invoices available</span>;
    }

    const sortedInvoices = [...invoices].sort(
        (a, b) => 
            a.day - b.day || a.startTime.substring(0, 2) - b.startTime.substring(0, 2)
    );

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            {sortedInvoices.map((invoice, i) => (
                <div key={i} style={{ marginBottom: "5px" }}>
                    <PDFViewer style={{ width: "100%", height: "500px" }}>
                        {InvoiceGenerate(invoice, venue)}
                    </PDFViewer>
                </div>
            ))}
        </div>
    );
}

export function InvoicesDownload({ invoices, venue, month, year }) {
    const [status, setStatus] = useState("");
    const zipRef = useRef(null);
    const [ready, setReady] = useState(false);

    const generateInvoices = async () => {
        if (!invoices || invoices.length === 0) {
            setStatus("Processing Documents / None Available");
            return;
        }

        setStatus("Generating PDF files...");

        const zip = new JSZip();

        const sortedInvoices = [...invoices].sort(
            (a, b) =>
                a.day - b.day || a.startTime.substring(0, 2) - b.startTime.substring(0, 2)
        );

        const pdfPromises = sortedInvoices.map(async (invoice) => {
            const dateParts = invoice.dateStr.split(', ')[1].split(' ');
            const monthName = dateParts[0];
            const day = dateParts[1].replace(',', '');
            const monthNumber = new Date(`${monthName} 1, 2000`).getMonth() + 1;
            const formattedDate = `${monthNumber}-${String(day).padStart(2, '0')}`;
            const fileName = `${formattedDate} ${invoice.startTime === "05:00" ? " #1" : " #2"} at ${invoice.startTime.replace(":", "-")}, ${venue.name === "Renaissance-Exchange" ? "Exchange" : venue.name} ${invoice.performers} Booking Invoice-`;
            const document = InvoiceGenerate(invoice, venue);
            const blob = await pdf(document).toBlob();

            zip.file(fileName + ".pdf", blob);
        });

        await Promise.all(pdfPromises);

        zipRef.current = zip;
        setReady(true);

        setStatus("** PDF files for zip successfully generated **");
    }
    
    const handleDownloadAll = async () => {
        if (!zipRef.current || !ready) return;

        const zipBlob = await zipRef.current.generateAsync({ type: "blob" });
        saveAs(
            zipBlob,
            `Exchange Booking Invoices-${month} ${year} V1 (Zip File)`
        );
    };

    useEffect(() => {
        setStatus("Generating PDF files...");
    }, [month, year]);
    
    useEffect(() => {
        if (!invoices) return;
        generateInvoices();
    }, [invoices]);

    return (
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Button variant="contained" onClick={handleDownloadAll}>
                Download all Invoices
            </Button>
            {status}
        </div>
    );
}

export function InvoiceSingle(event) {
    const splitCheck = event.splitCheck === true || event.splitCheck === "true";
    const { day, startTime, endTime, dateStr, dateForFile } =
        formatEventTimes(event);

    let performerText = event.performers;

    if (typeof performerText === "string") {
        performerText = performerText.split(",").map(p => p.trim());
    }

    if (Array.isArray(performerText)) {
        performerText = performerText.filter(p => p.length > 0);

        if (splitCheck) {
            const total = parseFloat(event.price);
            const each = (total / 2).toFixed(2);
            performerText =
                performerText.join(" & ") + ` (2 Checks-$${each} ea.)`;
        } else {
            performerText = performerText.join(", ");
        }
    }

    return {
        day,
        stage: event.stage,
        performers: event.performers,
        performerText,
        startTime,
        endTime,
        price: parseFloat(event.price + ".00").toFixed(2),
        dateStr,
        dateForFile,
        splitCheck
    };
}

/********************************
* ARTIST CONFIRMATION FUNCTIONS *
*********************************/

export function ConfirmationData(month, year, venueID){
    const [events, setEvents] = useState({});
    const [confirmations, setConfirmations] = useState([]);
    
    useEffect(() => {
        firebaseDb.child('database/events').orderByChild('venue_month_year').equalTo(venueID + "__" + month + "__" + year).on('value', (snapshot) => {  
            setEvents((snapshot.val() !== undefined) ? snapshot.val() : {});
        });
    }, [month, year, venueID])

    useEffect(() => {
        let tempConfirmations = [];

        if (events !== null && Object.keys(events).length !== 0) {
            tempConfirmations = Object.keys(events).map((key) => {
                const event = events[key];
                const { day, startTime, endTime, dateStr, dateForFile } = formatEventTimes(event);
        
                return {
                    day,
                    stage: events[key].stage,
                    startTime,
                    endTime,
                    dateStr,
                    dateForFile,
                    price: parseFloat(events[key].price + ".00").toFixed(2),
                };
            });
            tempConfirmations = tempConfirmations.filter(tempConfirmation => tempConfirmation["stage"] !== undefined);
        }
        setConfirmations(tempConfirmations);
    }, [events])

    return confirmations;
}

export function ConfirmationGenerate(confirmation, venue){
    const venueName = venue.name;
    const venueAddress = `${venue.address.street1}, ${venue.address.city}, ${venue.address.state} ${venue.address.zip}`;
    const venueCityState = `${venue.address.city}, ${venue.address.state}.`
    const venueText = {
        "Renaissance-Exchange": "The Renaissance/ Exchange Bar"
    };
    const fileTitle = `${venueName} Artist Confirmation ${confirmation.dateForFile}`;
    
    return (
        <Document title={fileTitle}>
            <Page style={styles.page}>
                <View style={styles.confirmationHeader}>
                    <Text style={{ marginBottom: "15px" }}>{venueName} {venueCityState}</Text>
                    <Text style={{ marginBottom: "15px" }}>Live performance contract/confirmation</Text>
                    <Text>musicmattersbookings.com</Text> 
                </View>
                <View>
                    <Text style={styles.paragraph}>
                    <Text wrap={false} style={styles.documentField}>{confirmation.stage}</Text> artist/performers agree to perform live music at {venueText[venueName] !== undefined ? venueText[venueName] : venueName}, {venueAddress} on the evening of <Text wrap={false} style={styles.documentField}>{confirmation.dateStr}</Text> between the listed hours of <Text wrap={false} style={styles.documentField}>{(confirmation.startTime.charAt(0) === "0") ? confirmation.startTime.substring(1) : confirmation.startTime}pm to {(confirmation.endTime.charAt(0) === "0") ? confirmation.endTime.substring(1) : confirmation.endTime}pm</Text> and {venueText[venueName] !== undefined ? venueText[venueName] : venueName} in {venueCityState} agrees to pay the above named artists <Text wrap={false} style={styles.documentField}>${confirmation.price}</Text> and said payment to be paid upon completion of this performance.
                    </Text>
                    <Text style={styles.paragraph}>
                        If for reasons beyond your control you are unable to make your scheduled confirmed performance time and date; It is our expectation that you will contact Mike Moody at <Text style={styles.phoneNumber}>619-307-5866</Text>. If he cannot be reached please call James Boheim at <Text style={styles.phoneNumber}>334-224-3814</Text>. This should be done at the earliest possible time, but no later than four hours prior to your set. Thank you in advance for complying to this request. MusicMattersBookings.com and The {venueName}.
                    </Text>
                </View>
            </Page>
        </Document>
    );
}

export function ConfirmationsViewer({ confirmations, venue }){
    if (!confirmations || confirmations.length === 0) {
        return <span>No confirmations available</span>;
    }

    const sortedConfirmations = [...confirmations].sort(
        (a, b) =>
            a.day - b.day || a.startTime.substring(0, 2) - b.startTime.substring(0, 2)
    );

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            {sortedConfirmations.map((confirmation, i) => (
                <div key={i} style={{ marginBottom: "5px" }}>
                    <PDFViewer style={{ width: "100%", height: "500px" }}>
                        {ConfirmationGenerate(confirmation, venue)}
                    </PDFViewer>
                </div>
            ))}
        </div>
    );
}

export function ConfirmationsDownload({ confirmations, venue, month, year }){
    const [status, setStatus] = useState("");
    const zipRef = useRef(null);
    const [ready, setReady] = useState(false);

    const generateConfirmations = async () => {
        if (!confirmations || confirmations.length === 0) {
            setStatus("Processing Documents / None Available");
            return;
        }

        setStatus("Generating PDF files...");

        const zip = new JSZip();

        const sortedConfirmations = [...confirmations].sort(
        (a, b) =>
            a.day - b.day || a.startTime.substring(0, 2) - b.startTime.substring(0, 2)
        );

        const pdfPromises = sortedConfirmations.map(async (confirmation) => {
            const fileName =(((venue.name === "Renaissance-Exchange") ? "Exchange" : venue.name) + "-Artist Confirmation-" + confirmation.dateForFile + ((confirmation.startTime === "05:00") ? " #1" : " #2"))
            const document = ConfirmationGenerate(confirmation, venue);
            const blob = await pdf(document).toBlob();

            zip.file(fileName + ".pdf", blob);
        });

        await Promise.all(pdfPromises);

        zipRef.current = zip;
        setReady(true);

        setStatus("*** PDF files for zip successfully generated ***");
    }

    const handleDownloadAll = async () => {
        const venueNameForZip = venue.name === "Renaissance-Exchange" ? "Exchange" : venue.name;

        if (!zipRef.current || !ready) return;

        const zipBlob = await zipRef.current.generateAsync({ type: "blob" });
        saveAs(
            zipBlob,
            `${venueNameForZip} ${month} ${year} Confirmations.zip`
        );
    };

    useEffect(() => {
        setStatus("Generating PDF files...");
    }, [month, year]);

    useEffect(() => {
        if (!confirmations) return;
        generateConfirmations();
    }, [confirmations]);

    return (
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Button variant="contained" onClick={handleDownloadAll}>
                Download all Confirmations
            </Button>
            {status}
        </div>
    );
}

export function ConfirmationSingle(event){
    const { day, startTime, endTime, dateStr, dateForFile } = formatEventTimes(event);
        
    return {
        day,
        stage: event.stage,
        startTime,
        endTime,
        dateStr,
        dateForFile,
        price: parseFloat(event.price + ".00").toFixed(2),
    };
}

/********************************
* SUBJECT LINE FUNCTIONS *
*********************************/

export function SubjectLineData(month, year, venueID){
    const [events, setEvents] = useState({});
    const [bookings, setBookings] = useState([]);

    useEffect(() => {
        firebaseDb.child('database/events').orderByChild('venue_month_year').equalTo(venueID + "__" + month + "__" + year).on('value', (snapshot) => {  
            setEvents((snapshot.val() !== undefined) ? snapshot.val() : {});
        });
    }, [month, year, venueID])

    useEffect(() => {
        let tempBookings = [];

        if (events !== null && Object.keys(events).length !== 0) {
            tempBookings = Object.keys(events).map((key) => {
                const event = events[key];
                const { day, startTime, endTime, dateStr } = formatEventTimes(event);
        
                return {
                    day,
                    stage: events[key].stage,
                    startTime,
                    endTime,
                    price: events[key].price + ".00",
                    dateStr
                };
            });
            tempBookings = tempBookings.filter(tempBooking => tempBooking["stage"] !== undefined);
        }
        
        setBookings(tempBookings);
    }, [events])

    return bookings;
}

export function SubjectLineGenerate({ bookings, venue, month, year }){
    const fileName = (venue.name + " Subject Line List " + month + ", " + year);

    if (!bookings.length) return null;

    return (
        <Document title={fileName}>
            <Page style={styles.page}>
                <View style={styles.heading}>
                    <Text style={styles.bookingTitle}>{venue.name} Live Music</Text>
                    <Text style={styles.bookingTitle}>Email Subject Line List</Text>
                    <Text style={styles.bookingTitle}>by Date</Text>
                    <Text style={styles.bookingTitle}>{month} {year}</Text> 
                </View>
                <View style={{ textAlign: 'center'}}>
                    <Text style={styles.bookingTitle}>{'\n'}Artist Confirmations Information{'\n'}</Text>
                </View>
                <View style={styles.table}>
                    <View style={[styles.subjectRow, styles.tableHeading]}>
                        <Text style={styles.subjectArtist}>Artist</Text>
                        <Text style={styles.artistInfo}>PDF File Name & Email Subject Line</Text>
                    </View>
                    {bookings.sort((a, b) => a.day - b.day || a.startTime.substring(0, 2) - b.startTime.substring(0, 2)).map((booking, i) => (
                        <View key={i} style={styles.subjectRow} wrap={false}>
                            <Text style={styles.subjectArtist}>{booking.day}-{booking.stage}</Text>
                            <Text textBreakStrategy="simple" style={styles.artistInfo}>
                                <Text style={styles.artistInfo}>
                                    {`${venue.name === "Renaissance-Exchange" ? "Exchange" : venue.name}-Artist Confirmation-${booking.dateStr} ${booking.startTime === "05:00" ? "#1" : "#2"}`}
                                </Text>
                            </Text>
                        </View>
                    ))}
                </View>
                <View style={{ textAlign: 'center'}}>
                    <Text style={styles.bookingTitle}>{'\n'}Invoices Information{'\n'}</Text>
                </View>
                <View style={styles.table}>
                    <View style={[styles.subjectRow, styles.tableHeading]}>
                        <Text style={styles.subjectArtist}>Artist</Text>
                        <Text style={styles.artistInfo}>PDF File Name & Email Subject Line</Text>
                    </View>
                    {bookings.sort((a, b) => a.day - b.day || a.startTime.substring(0, 2) - b.startTime.substring(0, 2)).map((booking, i) => (
                        <View key={i} style={styles.subjectRow} wrap={false}>
                            <Text style={styles.subjectArtist}>{booking.day}-{booking.stage}</Text>
                            <Text textBreakStrategy="simple" style={styles.artistInfo}>
                                <Text style={styles.artistInfo}>
                                    {`${venue.name === "Renaissance-Exchange" ? "Exchange" : venue.name} Booking Invoice-${booking.dateStr} ${booking.startTime === "05:00" ? "#1" : "#2"}`}
                                </Text>
                                {'\n'}
                            </Text>
                        </View>
                    ))}
                </View>
            </Page>
        </Document>
    );
}

export function SubjectLineViewer({ bookings, venue, month, year }){
    const pdfDoc = SubjectLineGenerate({ bookings, venue, month, year });
    return pdfDoc ? <PDFViewer style={styles.pdfViewer}>{pdfDoc}</PDFViewer> : <span>No subject lines available</span>;
}

export function SubjectLineDownload({ bookings, venue, month, year }){
    const pdfDoc = SubjectLineGenerate({ bookings, venue, month, year });
    const fileName = (venue.name + " Subject Line List " + month + ", " + year);

    return pdfDoc ? (
        <PDFDownloadLink document={pdfDoc} fileName={fileName}>
            {({ loading }) => (
                <Button variant="contained">
                    {loading ? 'Loading Document...' : 'Download'}
                </Button>
            )}
        </PDFDownloadLink>
    ) : <span>No subject lines available</span>;
}

// HELPER FUNCTIONS

function formatEventTimes(event) {
    const start = new Date(event.start);
    const end = new Date(event.end);

    const formatTime = (date) =>
        date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        .split(" ")[0]
        .replace(/AM|PM/g, "")
        .trim();

    const fourLetterDay = DAYS_OF_WEEK[start.toLocaleDateString('en-US', { weekday: 'long' })];

    const dateStr = `${fourLetterDay}, ${start.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    })}`;

    // Uses the shared dateFormatting.js utility instead of a local copy of
    // the month abbreviations — see dateFormatting.js for why this matters:
    // a drift between this file's abbreviations and Documents.js's has
    // silently broken GMass attachments before (twice: once for days, once
    // for months).
    const dateForFile = formatDayMonthDateYear(start);

    return {
        day: start.getDate(),
        startTime: formatTime(start),
        endTime: formatTime(end),
        dateStr,
        dateForFile
    };
}
